import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Box,
  Divider,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer/Footer";
import InvestorBanner from "../components/InvestorBanner/InvestorBanner";

const FAQs = () => {
  const footerRef = useRef(null);
  const [expandedPanel, setExpandedPanel] = useState(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const handlePanelChange = (panelId) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panelId : null);
  };

  const handleContactUsClick = () => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const faq = [
    {
      id: 1,
      question: "Do I own the land?",
      answer:
        "No. This is a lease-based model. You sign a sub-lease agreement for a specific plot for a defined tenure (e.g., 3 years). During this period, you have full rights to use the land and own 100% of the produce grown on it.",
    },
    {
      id: 2,
      question: "Is this an investment scheme?",
      answer:
        "No. This is a 'Farming as a Service' (FaaS) operational model. You lease farmland and hire us to manage farming activities. Your returns depend entirely on the quantity and market value of the harvest from your leased plot.",
    },
    {
      id: 3,
      question: "How is this different from profit-sharing?",
      answer:
        "In a profit-sharing model, the farm manager takes a percentage of the final profit. In our model, you pay a fixed managed farming fee and retain 100% of the harvest proceeds, giving you higher potential upside.",
    },
    {
      id: 4,
      question: "What happens to the harvest?",
      answer:
        "You have full control over the harvest. We can facilitate the sale of the produce at prevailing market rates and transfer the proceeds to you, or arrange doorstep delivery of the produce (logistics costs may apply).",
    },
    {
      id: 5,
      question: "How do I track my farm?",
      answer:
        "We provide a transparent digital platform where you can monitor farm activities, review yield reports, and view virtual tours of your farm plot.",
    },
    {
      id: 6,
      question:
        "What exactly is 'Farming as a Service' (FaaS) with InvestoFarms?",
      answer:
        "FaaS is an operational model where you lease a specific plot of farmland and we professionally manage the farming for you. You hold the lease and own 100% of the harvest, while we handle everything from soil preparation to harvesting.",
    },
    {
      id: 7,
      question: "Do I get legal ownership of the farmland?",
      answer:
        "No. You enter into a sub-lease agreement for a specific tenure (e.g., 3 years). This gives you legal rights to use the land and own the crops during the lease period, but it does not provide permanent land ownership.",
    },
    {
      id: 8,
      question: "How is this different from buying farmland myself?",
      answer:
        "Buying farmland requires high capital, extensive legal checks, and ongoing management. Our model offers a hassle-free experience with lower entry cost, no operational burden, and no long-term capital lock-in.",
    },
    {
      id: 9,
      question: "Who manages the farming activities?",
      answer:
        "Our in-house team of expert agronomists and operational managers oversees all activities. Local farmers are employed on fixed salaries to carry out daily tasks, ensuring professional and timely crop care.",
    },
    {
      id: 10,
      question: "Can I visit my farm plot?",
      answer:
        "Yes, absolutely. You can schedule farm visits to see your plot and its progress in person. We also provide virtual farm tours for remote access.",
    },
    {
      id: 11,
      question: "How will I know if the farming is going well?",
      answer:
        "We prioritize transparency. You get access to a digital dashboard to track progress and receive detailed quarterly reports covering crop health, yield estimates, and completed activities.",
    },
    {
      id: 12,
      question: "What happens to the harvest?",
      answer:
        "You own 100% of the harvest. Once harvested, you have two primary options:- Sale: We can facilitate the sale of your produce at prevailing market rates and transfer the net sale value to you. - Self-Consumption: In some cases, you can choose to have the fresh produce delivered to your doorstep (subject to logistics and crop type).",
    },
    {
      id: 13,
      question: "Is agricultural income taxable?",
      answer:
        "In India, agricultural income is generally exempt under Section 10(1) of the Income Tax Act, provided certain conditions are met. Since income is earned from the sale of produce on leased land, it typically qualifies. We recommend consulting a tax advisor for your specific case.",
    },
    {
      id: 14,
      question: "What fees do I pay?",
      answer:
        "You pay a Fixed Managed Farming Fee. This covers seeds, fertilizers, labor, technology, and professional farm management, ensuring predictable and transparent costs.",
    },
    {
      id: 15,
      question: "What if the crop fails due to weather or pests?",
      answer:
        "Agriculture involves natural risks. We mitigate these through crop insurance, precision farming, scientific planning, and data-driven crop rotation. While weather risks cannot be eliminated, our expert management significantly reduces operational risks.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(90deg, rgba(209,230,226,1) 0%, rgba(234,238,236,1) 95%)`,
      }}
    >
      <Helmet>
        <title>Frequently Asked Questions - InvestoFarms</title>
        <meta
          name="description"
          content="Explore our FAQ page for insights on investing in agriculture. Learn about opportunities, benefits, and strategies to grow your wealth through agricultural investments."
        />
      </Helmet>

      <Navbar onContactUsClick={handleContactUsClick} />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        <Typography
          sx={{
            fontSize: { xs: "28px", md: "36px" },
            fontWeight: "700",
            color: "#28947F",
            textAlign: "center",
            mb: 5,
          }}
        >
          Frequently Asked Questions
        </Typography>

        <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
          {faq.map((item) => (
            <Box key={item.id} sx={{ mb: 0.5 }}>
              <Accordion
                square
                elevation={0}
                expanded={expandedPanel === item.id}
                onChange={handlePanelChange(item.id)}
                sx={{
                  "&:before": { display: "none" },
                  background: "transparent",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: "#28947F" }} />}
                  sx={{ 
                    px: 0,
                    py: 0.5,
                    minHeight: "30px",
                    "&.Mui-expanded": {
                      minHeight: "30px",
                      mb: 0
                    },
                    "& .MuiAccordionSummary-content": {
                      my: 0.5
                    },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                      my: 0.5
                    }
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "15px", md: "16px" },
                      fontWeight: "600",
                      color: "#2c2c2c",
                    }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
                  <Typography 
                    sx={{ 
                      fontSize: { xs: "13px", md: "14px" },
                      lineHeight: 1.7,
                      color: "#494848ff",
                      textAlign: "justify"
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Divider sx={{ borderColor: "rgba(40, 148, 127, 0.2)" }} />
            </Box>
          ))}
        </Box>
      </Container>

      <Box sx={{ mt: "auto" }}>
        <InvestorBanner />
        <Footer ref={footerRef} />
      </Box>
    </Box>
  );
};

export default FAQs;